import React, { useContext, useEffect, useRef, useState } from "react";
import { Button, Form, Input, Popconfirm, Table, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./dailyReport.css";

const { Title } = Typography;

const EditableContext = React.createContext(null);

const formatListDate = (value) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("M/D/YYYY") : String(value);
};

const stripMeta = ({ key, date, dailyReportId, ...fields }) => fields;

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({
        ...record,
        ...values,
      });
    } catch (errInfo) {
      console.log("Save failed:", errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} is required.`,
          },
        ]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }
  return <td {...restProps}>{childNode}</td>;
};

const buildRowsFromReport = (dailyReport) =>
  (dailyReport?.meetings || []).map((meeting, index) => ({
    ...meeting,
    key: `${dailyReport._id || "day"}-${index}`,
    date: dailyReport.date,
    dailyReportId: dailyReport._id,
  }));

const buildRowsFromRange = (rangeReports) => {
  const rows = [];
  (rangeReports || []).forEach((report) => {
    (report.meetings || []).forEach((meeting, index) => {
      rows.push({
        ...meeting,
        key: `${report._id}-${index}`,
        date: report.date,
        dailyReportId: report._id,
      });
    });
  });
  return rows;
};

const MeetingsTable = ({
  dailyReport,
  updateDailyReportMutation,
  showDateColumn = false,
  rangeReports = null,
}) => {
  const isListMode = showDateColumn && Array.isArray(rangeReports);
  const [dataSource, setDataSource] = useState(
    isListMode ? buildRowsFromRange(rangeReports) : buildRowsFromReport(dailyReport)
  );
  const [count, setCount] = useState(dailyReport?.meetings?.length || 0);

  useEffect(() => {
    if (isListMode) {
      setDataSource(buildRowsFromRange(rangeReports));
    } else {
      setDataSource(buildRowsFromReport(dailyReport));
      setCount(dailyReport?.meetings?.length || 0);
    }
  }, [dailyReport, rangeReports, isListMode]);

  const persistReportRows = (rows, reportId) => {
    const meetings = rows
      .filter((item) => item.dailyReportId === reportId)
      .map(stripMeta);
    updateDailyReportMutation({
      dailyReport: { meetings },
      dailyReportId: reportId,
    });
  };

  const handleDelete = (key) => {
    const row = dataSource.find((item) => item.key === key);
    const newData = dataSource.filter((item) => item.key !== key);
    setDataSource(newData);
    if (row?.dailyReportId) {
      persistReportRows(newData, row.dailyReportId);
    }
  };

  const defaultColumns = [
    ...(showDateColumn
      ? [
          {
            title: "Date",
            dataIndex: "date",
            width: "20%",
            render: (value) => formatListDate(value),
          },
        ]
      : []),
    {
      title: "Meeting",
      dataIndex: "meeting",
      width: showDateColumn ? "80%" : "100%",
      editable: true,
    },
    {
      dataIndex: "operation",
      render: (_, record) =>
        dataSource.length >= 1 ? (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => handleDelete(record.key)}
          >
            <Button
              danger
              shape="circle"
              type="text"
              icon={<CloseOutlined />}
            />
          </Popconfirm>
        ) : null,
    },
  ];

  const handleAdd = () => {
    if (isListMode || !dailyReport?._id) return;
    const newData = {
      key: `${dailyReport._id}-${count}`,
      meeting: "",
      date: dailyReport.date,
      dailyReportId: dailyReport._id,
    };
    const next = [...dataSource, newData];
    setDataSource(next);
    setCount(count + 1);
    persistReportRows(next, dailyReport._id);
  };

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
    if (row.dailyReportId) {
      persistReportRows(newData, row.dailyReportId);
    }
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <div>
      <div className="flex justify-between mt-6">
        <Title level={3}>Meetings</Title>
        {!isListMode && (
          <Button onClick={handleAdd} type="primary" className="mb-4">
            Add a Meeting
          </Button>
        )}
      </div>
      <Table
        components={components}
        rowClassName={() => "editable-row"}
        bordered
        dataSource={dataSource}
        columns={columns}
        pagination={{ position: ["none", "none"] }}
      />
    </div>
  );
};
export default MeetingsTable;
